import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowLeft,
  ArrowRight,
  CalendarDays,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Clock,
  Loader2,
  Video,
} from "lucide-react";
import whatsappLogo from "@/assets/whatsapp-icon.webp";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import nielsPhoto from "@/assets/niels-jansen.png.asset.json";
import { newEventId, sendServerEvent, trackMetaEvent } from "@/components/MetaPixel";

interface Slot {
  time: string;
  start: string;
  available: boolean;
}

interface BookingPlannerProps {
  /** Optional eyebrow above the title, e.g. "[01] Kennismaking" */
  eyebrow?: string;
  className?: string;
}

const WEEKDAY_LABELS = ["ma", "di", "wo", "do", "vr", "za", "zo"];
const MONTHS = [
  "januari",
  "februari",
  "maart",
  "april",
  "mei",
  "juni",
  "juli",
  "augustus",
  "september",
  "oktober",
  "november",
  "december",
];

function toDateStr(d: Date) {
  const m = `${d.getMonth() + 1}`.padStart(2, "0");
  const day = `${d.getDate()}`.padStart(2, "0");
  return `${d.getFullYear()}-${m}-${day}`;
}

function startOfDay(d: Date) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function addDays(d: Date, n: number) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
}

/** Eerstvolgende werkdag vanaf d (inclusief d) */
function nextWeekday(d: Date) {
  const x = startOfDay(d);
  while (x.getDay() === 0 || x.getDay() === 6) x.setDate(x.getDate() + 1);
  return x;
}

/** 5 opeenvolgende werkdagen vanaf start */
function weekdaysFrom(start: Date) {
  const out: Date[] = [];
  let cur = nextWeekday(start);
  while (out.length < 5) {
    out.push(cur);
    cur = nextWeekday(addDays(cur, 1));
  }
  return out;
}

type Step = "intro" | "date" | "done";


const BookingPlanner = ({ eyebrow = "[01] Kennismaking", className = "" }: BookingPlannerProps) => {
  const { toast } = useToast();
  const today = useMemo(() => startOfDay(new Date()), []);
  const maxDate = useMemo(
    () => new Date(today.getFullYear(), today.getMonth(), today.getDate() + 60),
    [today],
  );

  const [weekStart, setWeekStart] = useState(() => nextWeekday(new Date()));
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [slots, setSlots] = useState<Slot[]>([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const [step, setStep] = useState<Step>("intro");

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [company, setCompany] = useState("");
  const [notes, setNotes] = useState("");
  const [honeypot, setHoneypot] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState<
    { when: string; meetLink: string | null; bookingId: string | null } | null
  >(null);
  const [cancelling, setCancelling] = useState(false);
  const [cancelled, setCancelled] = useState(false);

  const weekDays = useMemo(() => weekdaysFrom(weekStart), [weekStart]);
  const canGoPrevWeek = weekStart > nextWeekday(today);



  const isSelectable = useCallback(
    (d: Date) => {
      const day = d.getDay();
      if (day === 0 || day === 6) return false;
      if (d < today) return false;
      if (d > maxDate) return false;
      // Laatste slot start om 16:30; met 2 uur voorbereidingstijd vervalt de dag daarna.
      const lastBookable = new Date(
        d.getFullYear(),
        d.getMonth(),
        d.getDate(),
        16,
        30,
      ).getTime() - 120 * 60 * 1000;
      if (Date.now() > lastBookable) return false;
      return true;
    },
    [today, maxDate],
  );

  const slotCache = useRef(new Map<string, Slot[]>());
  const inFlight = useRef(new Map<string, Promise<Slot[]>>());

  const fetchSlots = useCallback(async (date: string): Promise<Slot[]> => {
    const cached = slotCache.current.get(date);
    if (cached) return cached;
    const pending = inFlight.current.get(date);
    if (pending) return pending;
    const req = supabase.functions
      .invoke("booking-availability", { body: { date } })
      .then(({ data, error }) => {
        if (error) throw error;
        const result: Slot[] = data?.slots ?? [];
        slotCache.current.set(date, result);
        return result;
      })
      .catch((err) => {
        console.error("Kon beschikbaarheid niet laden:", err);
        return [] as Slot[];
      })
      .finally(() => {
        inFlight.current.delete(date);
      });
    inFlight.current.set(date, req);
    return req;
  }, []);

  const loadSlots = useCallback(
    async (date: string) => {
      const cached = slotCache.current.get(date);
      if (cached) {
        setSlots(cached);
        setLoadingSlots(false);
        return;
      }
      setLoadingSlots(true);
      setSlots([]);
      const result = await fetchSlots(date);
      setSlots(result);
      setLoadingSlots(false);
    },
    [fetchSlots],
  );

  useEffect(() => {
    if (selectedDate) loadSlots(selectedDate);
  }, [selectedDate, loadSlots]);

  // Beschikbaarheid van de zichtbare week alvast ophalen zodat klikken direct laadt.
  useEffect(() => {
    weekDays.filter(isSelectable).forEach((d) => {
      void fetchSlots(toDateStr(d));
    });
  }, [weekDays, isSelectable, fetchSlots]);

  const book = async (time: string) => {
    if (honeypot) return;
    if (!selectedDate) return;
    setSubmitting(true);
    setSelectedTime(time);
    try {
      const { data, error } = await supabase.functions.invoke("booking-create", {
        body: {
          name: name.trim(),
          email: email.trim(),
          phone: phone.trim(),
          company: company.trim(),
          notes: notes.trim(),
          date: selectedDate,
          time,
          website: honeypot,
        },
      });
      if (error || data?.error) throw new Error(data?.error || "Boeking mislukt");
      setConfirmation({
        when: data.when,
        meetLink: data.meetLink ?? null,
        bookingId: data.bookingId ?? null,
      });
      setStep("done");

      // Conversie: gesprek ingepland (browser + server met hetzelfde event-ID)
      const eventId = newEventId();
      const [firstName, ...rest] = name.trim().split(" ");
      const conversion = { value: 250, currency: "EUR", content_name: "Kennismakingsgesprek" };
      trackMetaEvent("Schedule", conversion, eventId);
      console.info(
        `[Meta] Schedule browser-event ${window.fbq ? "verstuurd" : "overgeslagen (geen toestemming)"} — event_id ${eventId}`,
      );


      void sendServerEvent({
        eventName: "Schedule",
        eventId,
        email: email.trim(),
        phone: phone.trim() || undefined,
        firstName,
        lastName: rest.join(" ") || undefined,
        value: 250,
        currency: "EUR",
      });
    } catch (err) {
      console.error("Boeking mislukt:", err);
      toast({
        title: "Inplannen mislukt",
        description: "Dit tijdslot is mogelijk net bezet. Kies een ander moment.",
        variant: "destructive",
      });
      setSelectedTime(null);
      setStep("date");

      if (selectedDate) {
        slotCache.current.delete(selectedDate);
        loadSlots(selectedDate);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const cancelBooking = async () => {
    if (!confirmation?.bookingId) return;
    setCancelling(true);
    try {
      const { data, error } = await supabase.functions.invoke("booking-cancel", {
        body: { id: confirmation.bookingId },
      });
      if (error || data?.error) throw new Error(data?.error || "Annuleren mislukt");
      setCancelled(true);
    } catch (err) {
      console.error("Annuleren mislukt:", err);
      toast({
        title: "Annuleren mislukt",
        description: "Mail info@nextconvert.nl en we regelen het direct.",
        variant: "destructive",
      });
    } finally {
      setCancelling(false);
    }
  };

  const handleIntro = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !company.trim()) {
      toast({ title: "Vul je naam, e-mailadres en webshop URL in", variant: "destructive" });
      return;
    }
    setStep("date");
  };

  const selectedLabel = selectedDate
    ? (() => {
        const [y, m, d] = selectedDate.split("-").map(Number);
        return `${d} ${MONTHS[m - 1]} ${y}`;
      })()
    : null;

  

  const stepIndex = step === "intro" ? 1 : 2;

  return (
    <div className="w-full">
    <div
      className={`rounded-2xl md:rounded-3xl bg-white text-primary overflow-hidden border border-primary/10 shadow-[0_20px_60px_-30px_rgba(13,35,64,0.45)] grid grid-cols-1 md:grid-cols-[minmax(260px,340px)_1fr] ${className}`}
    >
      {/* Host panel */}
      <div className="p-6 md:p-8 flex flex-col justify-between gap-8 bg-white border-b md:border-b-0 md:border-r border-primary/10">
        <div>
          <div className="flex items-center gap-3">
            <div className="relative">
              <img
                src={nielsPhoto.url}
                alt="Niels Jansen, oprichter Nextconvert"
                loading="lazy"
                decoding="async"
                className="w-16 h-16 rounded-full object-cover border border-primary/10"
              />
              <span className="absolute bottom-0 right-0 w-4 h-4 rounded-full bg-green-500 border-2 border-white" />
            </div>
            <div>
              <p className="font-semibold leading-tight">Niels Jansen</p>
              <p className="text-primary/60 text-sm">Oprichter Nextconvert</p>
            </div>
          </div>

          {/* Snel contact - desktop */}
          <div className="hidden md:block mt-6 border-t border-primary/10 pt-5">
            <p className="text-sm font-semibold mb-3 text-center">Snel in contact komen</p>
            <a
              href="https://wa.me/31620192358"
              target="_blank"
              rel="noopener noreferrer"
              className="w-full rounded-xl border border-primary/15 px-3 py-3 text-center text-sm font-medium hover:border-primary/50 transition-colors inline-flex items-center justify-center gap-2"
            >
              <img src={whatsappLogo} alt="" className="w-4 h-4" /> Stuur mij een appje!
            </a>
          </div>
        </div>

        <div className="space-y-2 text-sm text-primary/70 border-t border-primary/10 pt-4">
          <p className="flex items-center gap-2">
            <Clock className="w-4 h-4" /> 30 minuten
          </p>
          <p className="flex items-center gap-2">
            <Video className="w-4 h-4" /> Google Meet
          </p>
          <p className="flex items-center gap-2">
            <CalendarDays className="w-4 h-4" /> Ma t/m vr, 09:00-12:00 &amp; 13:00-17:00
          </p>
        </div>
      </div>

      {/* Interactive panel */}
      <div className="bg-white text-primary p-5 md:p-7">
        {step !== "done" && (
          <div className="flex items-center gap-2 mb-5">
            {[1, 2].map((i) => (
              <span
                key={i}
                className={`h-1.5 flex-1 rounded-full transition-colors ${
                  i <= stepIndex ? "bg-accent" : "bg-primary/10"
                }`}
              />
            ))}
            <span className="text-xs text-primary/50 ml-2 whitespace-nowrap">Stap {stepIndex}/2</span>
          </div>
        )}

        {step === "done" && confirmation ? (
          cancelled ? (
            <div className="h-full flex flex-col items-center justify-center text-center py-10">
              <h4 className="text-xl font-semibold mb-2">Je afspraak is geannuleerd</h4>
              <p className="text-primary/70 text-sm">
                Je ontvangt een bevestiging per e-mail. Je kunt hieronder een nieuw moment kiezen.
              </p>
              <Button
                onClick={() => {
                  setCancelled(false);
                  setConfirmation(null);
                  setSelectedTime(null);
                  setStep("date");
                  if (selectedDate) loadSlots(selectedDate);
                }}
                className="mt-5 bg-accent hover:bg-accent/90 text-accent-foreground"
              >
                Nieuw moment kiezen
              </Button>
            </div>
          ) : (
          <div className="h-full flex flex-col items-center justify-center text-center py-10">
            <CheckCircle2 className="w-14 h-14 text-green-600 mb-4" />
            <h4 className="text-xl font-semibold mb-2">Je afspraak staat genoteerd</h4>
            <p className="text-primary/70 mb-4">{confirmation.when}</p>
            {confirmation.meetLink && (
              <Button asChild className="bg-accent hover:bg-accent/90 text-accent-foreground">
                <a href={confirmation.meetLink} target="_blank" rel="noopener noreferrer">
                  Google Meet-link openen
                </a>
              </Button>
            )}
            <p className="text-primary/60 text-sm mt-4">
              Je ontvangt een bevestiging per e-mail met de meeting-link.
            </p>
            {confirmation.bookingId && (
              <button
                type="button"
                onClick={cancelBooking}
                disabled={cancelling}
                className="mt-3 text-sm text-primary/50 underline underline-offset-4 hover:text-primary disabled:opacity-50 inline-flex items-center gap-2"
              >
                {cancelling ? (
                  <>
                    <Loader2 className="w-3.5 h-3.5 animate-spin" /> Annuleren...
                  </>
                ) : (
                  "Meeting annuleren"
                )}
              </button>
            )}
          </div>
          )
        ) : step === "intro" ? (
          <form onSubmit={handleIntro} className="space-y-4">
            <div>
              <h4 className="text-lg font-semibold">Laten we beginnen</h4>
              <p className="text-primary/60 text-sm">Vul je gegevens in om verder te gaan.</p>
            </div>
            <input
              type="text"
              name="website"
              value={honeypot}
              onChange={(e) => setHoneypot(e.target.value)}
              className="hidden"
              tabIndex={-1}
              autoComplete="off"
            />
            <div>
              <Label htmlFor="bp-name" className="text-sm mb-1.5 block">
                Naam <span className="text-accent">*</span>
              </Label>
              <Input id="bp-name" value={name} onChange={(e) => setName(e.target.value)} required className="h-11" placeholder="Voornaam en achternaam" />
            </div>
            <div>
              <Label htmlFor="bp-email" className="text-sm mb-1.5 block">
                E-mailadres <span className="text-accent">*</span>
              </Label>
              <Input id="bp-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="h-11" placeholder="jij@bedrijf.nl" />
            </div>
            <div>
              <Label htmlFor="bp-url" className="text-sm mb-1.5 block">
                Webshop URL <span className="text-accent">*</span>
              </Label>
              <Input
                id="bp-url"
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                required
                className="h-11"
                placeholder="bijv. jouwwebshop.nl"
              />
            </div>
            <Button type="submit" className="w-full h-11 bg-accent hover:bg-accent/90 text-accent-foreground font-semibold">
              Kies een moment <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </form>
        ) : (

          <div>
            <button
              type="button"
              onClick={() => setStep("intro")}
              className="inline-flex items-center gap-1.5 text-sm text-primary/60 hover:text-primary mb-4"
            >
              <ArrowLeft className="w-4 h-4" /> Terug
            </button>

            {/* Week navigation */}
            <div className="flex items-center justify-between mb-4">
              <span className="font-semibold text-base md:text-lg">
                {MONTHS[weekDays[0].getMonth()]} {weekDays[0].getFullYear()}
              </span>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  disabled={!canGoPrevWeek}
                  onClick={() => {
                    const prev = addDays(weekStart, -7);
                    setWeekStart(nextWeekday(prev < today ? today : prev));
                  }}
                  className="w-9 h-9 rounded-full border border-primary/15 flex items-center justify-center hover:bg-primary/5 disabled:opacity-30"
                  aria-label="Vorige week"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => setWeekStart(nextWeekday(addDays(weekStart, 7)))}
                  className="w-9 h-9 rounded-full border border-primary/15 flex items-center justify-center hover:bg-primary/5"
                  aria-label="Volgende week"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>

              </div>
            </div>

            {/* Days strip */}
            <div className="grid grid-cols-5 gap-1.5 md:gap-2 mb-6">
              {weekDays.map((d) => {
                const str = toDateStr(d);
                const selectable = isSelectable(d);
                const isSelected = selectedDate === str;
                return (
                  <button
                    key={str}
                    type="button"
                    disabled={!selectable}
                    onClick={() => {
                      setSelectedDate(str);
                      setSelectedTime(null);
                    }}
                    className={`rounded-xl border py-2.5 md:py-3 flex flex-col items-center gap-0.5 transition-colors ${
                      isSelected
                        ? "bg-primary border-primary text-white"
                        : selectable
                          ? "bg-white border-primary/15 text-primary hover:border-primary/50"
                          : "bg-cream/60 border-transparent text-primary/25 cursor-not-allowed"
                    }`}
                  >
                    <span className="text-[11px] uppercase tracking-wide opacity-70">
                      {WEEKDAY_LABELS[(d.getDay() + 6) % 7]}
                    </span>
                    <span className="text-lg md:text-xl font-semibold leading-none">{d.getDate()}</span>
                  </button>
                );
              })}
            </div>

            {/* Time blocks */}
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-semibold">{selectedLabel ?? "Kies een dag"}</p>
              <span className="text-xs text-primary/50">Nederland (CET) · 30 min</span>
            </div>
            {!selectedDate ? (
              <p className="text-sm text-primary/50">Selecteer eerst een dag hierboven.</p>
            ) : loadingSlots ? (
              <div className="flex items-center gap-2 text-sm text-primary/60">
                <Loader2 className="w-4 h-4 animate-spin" /> Tijden laden...
              </div>
            ) : slots.length === 0 ? (
              <p className="text-sm text-primary/50">
                Geen vrije tijden op deze dag. Kies een andere dag.
              </p>
            ) : (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 md:gap-2.5">
                  {slots.map((s) => (
                    <button
                      key={s.time}
                      type="button"
                      onClick={() => setSelectedTime(s.time)}
                      className={`h-11 rounded-xl border text-sm font-medium transition-colors inline-flex items-center justify-center gap-2 ${
                        selectedTime === s.time
                          ? "bg-primary border-primary text-white"
                          : "bg-white border-primary/15 hover:border-primary"
                      }`}
                    >
                      {s.time}
                    </button>
                  ))}
                </div>
                <Button
                  type="button"
                  disabled={!selectedTime || submitting}
                  onClick={() => selectedTime && book(selectedTime)}
                  className="w-full h-11 mt-5 bg-accent hover:bg-accent/90 text-accent-foreground font-semibold disabled:opacity-50"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Bezig met inplannen...
                    </>
                  ) : (
                    <>
                      Gesprek inplannen <ArrowRight className="ml-2 h-4 w-4" />
                    </>
                  )}
                </Button>
              </>
            )}
          </div>
        )}


      </div>
    </div>

    {/* Snel contact - mobiel */}
    <div className="md:hidden mt-5">
      <p className="text-sm font-semibold text-white mb-3 text-center">Snel contact?</p>
      <a
        href="https://wa.me/31620192358"
        target="_blank"
        rel="noopener noreferrer"
        className="w-full rounded-xl bg-white border border-primary/15 shadow-sm px-3 py-4 text-center text-sm font-medium text-primary hover:border-primary/50 transition-colors inline-flex items-center justify-center gap-2"
      >
        <img src={whatsappLogo} alt="" className="w-4 h-4" /> Stuur mij een appje!
      </a>
    </div>
    </div>
  );
};

export default memo(BookingPlanner);
